import { CueStartMode, CueType } from '~shared/rundown/cue'
import type { Cue } from '~shared/rundown/cue'
import type { GetCue, IndexedCue } from '~shared/utils'

/**
 * Cue rules that belong to the editor, not to the data.
 *
 * These encode what the UI decides to show: which row the timing widgets treat
 * as the neighbor, which rows lead into a scheduled start, what a collapsed
 * group hides. The backend answers the same-sounding questions differently
 * (`RunnerUtils.getNextCueId` skips cues marked skipDuringShow, these do not),
 * so keeping them here is what stops the two from being conflated.
 */

/** Closest playable neighbors of a row, in document order. */
export type PlayableNeighbors = {
  prevId: string | null
  nextId: string | null
}

/**
 * Map every row to its closest playable predecessor and successor.
 *
 * Walks the sorted list once in each direction, so the whole rundown costs
 * O(N). Searching per row instead walks the list again for every row, which is
 * O(N^2) across a list and was the single biggest cost on a large rundown.
 *
 * Headings and groups answer for the playable cues around them. Rows whose
 * document has not arrived are absent from the map.
 */
export function buildPlayableNeighborsMap(
  sortedCues: readonly Cue[],
): Record<string, PlayableNeighbors> {
  const neighbors: Record<string, PlayableNeighbors> = {}

  let prevId: string | null = null
  for (const cue of sortedCues) {
    neighbors[cue.id] = { prevId, nextId: null }
    if (cue.type === CueType.CUE) prevId = cue.id
  }

  let nextId: string | null = null
  for (let i = sortedCues.length - 1; i >= 0; i--) {
    const cue = sortedCues[i]
    if (!cue) continue

    const entry = neighbors[cue.id]
    if (entry) entry.nextId = nextId
    if (cue.type === CueType.CUE) nextId = cue.id
  }

  return neighbors
}

/**
 * Next cue after `currentCueId` that starts at a fixed time.
 *
 * A hard start is a commitment the show cannot slip past, so the UI counts down
 * to it. Searching strictly *after* the running cue is deliberate: a fixed cue
 * that is already running is not something still to come.
 *
 * Pass `currentCueId: null` for a rundown that is not running, which searches
 * from the top.
 */
export function findNextHardStartCueId(
  orderedCueIds: readonly string[],
  getCue: GetCue,
  currentCueId: string | null,
): string | null {
  const currentIndex = currentCueId ? orderedCueIds.indexOf(currentCueId) : -1

  for (let i = currentIndex + 1; i < orderedCueIds.length; i++) {
    const cueId = orderedCueIds[i]
    if (!cueId) continue

    const cue = getCue(cueId)
    if (cue?.type === CueType.CUE && cue.startMode === CueStartMode.FIXED) {
      return cueId
    }
  }
  return null
}

/**
 * Ids of rows that lead into a soft-scheduled cue.
 *
 * A row is in the path when the first playable cue at or below it is
 * soft-scheduled: flexible start mode plus `scheduled`. Headings and groups
 * answer for the next playable cue below them. The first playable cue is
 * excluded, since it starts from the rundown start time whatever its stored
 * start mode says.
 *
 * One backward pass, so the whole rundown costs O(N). Keyed by cue id rather
 * than returned as a list, because every row reads its own answer and a list
 * would make each read O(N). Rows whose document has not arrived are absent.
 */
export function buildSoftSchedulePath(
  orderedCueIds: readonly string[],
  getCue: GetCue,
  firstCueId: string | null,
): Record<string, true> {
  const path: Record<string, true> = {}
  let nextPlayableId: string | null = null

  for (let i = orderedCueIds.length - 1; i >= 0; i--) {
    const cueId = orderedCueIds[i]
    if (!cueId) continue

    const cue = getCue(cueId)
    if (cue?.type === CueType.CUE) nextPlayableId = cueId

    if (!cue || !nextPlayableId || nextPlayableId === firstCueId) continue

    const target = cue.type === CueType.CUE ? cue : getCue(nextPlayableId)
    if (
      target?.startMode === CueStartMode.FLEXIBLE &&
      target.scheduled === true
    ) {
      path[cueId] = true
    }
  }

  return path
}

/**
 * Drop cues skipped during the show, and the children of skipped groups.
 *
 * Used by the output surfaces (cue list, prompter), where a skipped cue should
 * be invisible. The editor keeps them and marks them instead, so this is a
 * filter at the read site rather than a flag on the store.
 */
export function filterSkippedIndexedCues(
  indexedCues: readonly IndexedCue[],
  getCue: GetCue,
): IndexedCue[] {
  return indexedCues.filter(({ id, parentId }) => {
    if (getCue(id)?.settings?.skipDuringShow) return false
    if (parentId && getCue(parentId)?.settings?.skipDuringShow) return false
    return true
  })
}

/**
 * Drop the children of collapsed groups, keeping the groups themselves.
 *
 * The list renders this so collapsed children actually unmount. Hiding them
 * with CSS instead left every hidden richtext editor in the DOM, and the
 * style/layout pass over them made collapsing a large group visibly slow.
 *
 * Returns the input untouched when nothing is collapsed, so the derived store
 * can tell "no change" by identity.
 */
export function filterCollapsedIndexedCues(
  indexedCues: IndexedCue[],
  collapsedGroupIds: readonly string[],
): IndexedCue[] {
  if (collapsedGroupIds.length === 0) return indexedCues

  const collapsed = new Set(collapsedGroupIds)
  return indexedCues.filter(
    ({ parentId }) => !parentId || !collapsed.has(parentId),
  )
}

/** Ids of every group cue, in document order. Drives "collapse all". */
export function groupCueIds(sortedCues: readonly Cue[]): string[] {
  return sortedCues
    .filter((cue) => cue.type === CueType.GROUP)
    .map((cue) => cue.id)
}
